import React, { useState } from 'react'
import { ShieldCheck, ShieldAlert, ChevronDown, ChevronUp } from 'lucide-react'
import { nilGoLabel, type NilCompliance } from '@adthlete/common/utils/nilCompliance'
import ComplianceTab from '@/components/collaboration-detail/ComplianceTab'
import { Card, Eyebrow, Pill, primaryCls } from './ui'

/**
 * NIL Go on a $600+ deal: where the filing stands, the deadline, and the filing itself
 * (contract scan, the NIL Go form, the confirmation number, the school report) inline.
 */
export const NilGoCard: React.FC<{
  deal: any
  compliance: NilCompliance
  defaultOpen?: boolean
}> = ({ deal, compliance: c, defaultOpen }) => {
  const [open, setOpen] = useState(!!defaultOpen)
  const cleared = c.state === 'cleared'
  const overdue = c.state === 'overdue'
  const tone = cleared ? 'lime' : overdue ? 'amber' : 'plain'
  // Their manager may have filled it in for them, or filed it
  const preparedBy = deal.nilGoFields?.preparedBy as { name?: string; at?: string } | undefined
  const filedBy = deal.nilGoFields?.filedBy as { name?: string } | undefined
  const on = (iso?: string) =>
    iso
      ? ` on ${new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`
      : ''
  return (
    <Card tone={tone as any}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <Eyebrow tone={cleared ? 'lime' : overdue ? 'amber' : 'muted'}>NIL Go</Eyebrow>
          <p className="mt-1 flex items-center gap-2 font-bold text-foreground">
            {cleared ? (
              <ShieldCheck className="h-4 w-4 text-[#4D7C0F]" />
            ) : (
              <ShieldAlert className="h-4 w-4 text-[#B45309]" />
            )}
            {cleared
              ? 'Cleared'
              : overdue
                ? 'Filing is overdue'
                : preparedBy
                  ? 'Ready for you to file'
                  : 'File your NIL Go report'}
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            {cleared
              ? `Confirmation ${c.confirmationNumber ?? ''}${filedBy?.name ? `, filed for you by ${filedBy.name}` : ''}. ${c.schoolReported ? 'Your school has the report.' : 'We send it to your school next.'}`
              : preparedBy
                ? `${preparedBy.name ?? 'Your manager'} filled it in for you${on(preparedBy.at)}. Check the details, file it in NIL Go, then add the confirmation number.`
                : `Deals of $600 or more must be cleared before content goes live. It takes a few minutes, and we report it to your school for you.`}
          </p>
        </div>
        <Pill tone={cleared ? 'green' : overdue ? 'red' : c.inProgress ? 'blue' : 'amber'}>
          {c.inProgress && !cleared ? 'In progress' : nilGoLabel(c)}
        </Pill>
      </div>
      {!cleared && !open ? (
        <button type="button" className={`mt-3 ${primaryCls}`} onClick={() => setOpen(true)}>
          {preparedBy ? 'Check and file' : c.inProgress ? 'Continue NIL Go' : 'Start NIL Go'}
        </button>
      ) : null}
      {cleared || open ? (
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-muted-foreground"
        >
          {open ? 'Hide details' : 'Show details'}
          {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>
      ) : null}
      {open ? (
        <div className="mt-3 border-t border-border pt-3">
          <ComplianceTab campaign={deal} />
        </div>
      ) : null}
    </Card>
  )
}

export default NilGoCard
